import { phoneSchema } from '@hemmafixa/shared';
import { Button, Checkbox, FormField, Input, Label } from '@hemmafixa/ui';
import { MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLogout, useSession } from '@/features/auth/api';
import { AuthPage } from '@/features/auth/auth-page';
import { fill, useI18n } from '@/i18n/context';
import { useProviderSignup, useStartProvider } from './api';

/**
 * Become a provider (P2-09). Signed out: name, email and phone, then the next step comes by email. Signed in: one
 * button adds the provider role to the account. The landing page's become-a-provider page links here (P6-07).
 */
export function ProviderSignupPage() {
  const { t } = useI18n();
  const session = useSession();
  const p = t.providerSignup;

  if (session.isPending) return null;
  return (
    <AuthPage
      title={session.data ? p.signedInTitle : p.title}
      description={session.data ? undefined : p.description}
      footer={
        !session.data && (
          <Link to="/login" className="font-semibold text-foreground underline-offset-4 hover:underline">
            {t.auth.backToLogin}
          </Link>
        )
      }
    >
      {session.data ? (
        <ContinueAsProvider name={session.data.name} roles={session.data.roles} />
      ) : (
        <SignupForm />
      )}
    </AuthPage>
  );
}

function ContinueAsProvider({ name, roles }: { name: string; roles: string[] }) {
  const { t } = useI18n();
  const p = t.providerSignup;
  const navigate = useNavigate();
  const start = useStartProvider();
  const logout = useLogout();

  if (roles.includes('provider')) {
    return (
      <div className="grid gap-4">
        <p className="text-sm">{p.alreadyProvider}</p>
        <Button asChild size="lg">
          <Link to="/provider/onboarding">{p.toProvider}</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="grid gap-4">
      <p className="text-sm">{fill(p.signedInText, { name })}</p>
      {start.isError && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
          {p.failed}
        </p>
      )}
      <Button
        size="lg"
        loading={start.isPending}
        onClick={() => start.mutate(undefined, { onSuccess: () => void navigate('/provider/onboarding') })}
      >
        {p.continue}
      </Button>
      <Button variant="ghost" onClick={() => logout.mutate()}>
        {p.notYou}
      </Button>
    </div>
  );
}

function SignupForm() {
  const { t, language } = useI18n();
  const p = t.providerSignup;
  const signup = useProviderSignup();
  const [form, setForm] = useState({ name: '', email: '', phone: '', acceptTerms: false });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

  if (signup.isSuccess) {
    return (
      <div className="flex gap-3 text-sm">
        <MailCheck className="size-5 shrink-0 text-success" aria-hidden />
        <div className="grid gap-1">
          <p className="font-semibold">{p.sentTitle}</p>
          <p role="status">{fill(p.sentText, { email: signup.variables.email })}</p>
        </div>
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = {
      name: form.name.trim().length < 2 ? p.nameInvalid : undefined,
      email: /^\S+@\S+\.\S+$/.test(form.email.trim()) ? undefined : t.auth.emailInvalid,
      phone: phoneSchema.safeParse(form.phone).success ? undefined : p.phoneInvalid,
      acceptTerms: form.acceptTerms ? undefined : p.termsRequired,
    };
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    signup.mutate({
      name: form.name,
      email: form.email,
      phone: form.phone,
      locale: language,
      acceptTerms: true,
    });
  };

  return (
    <form className="grid gap-4" onSubmit={submit} noValidate>
      {signup.isError && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
          {p.failed}
        </p>
      )}
      <FormField label={p.name} error={errors.name} required>
        <Input
          autoComplete="name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
      </FormField>
      <FormField label={t.auth.email} error={errors.email} required>
        <Input
          type="email"
          autoComplete="email"
          inputMode="email"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />
      </FormField>
      <FormField label={p.phone} error={errors.phone} required>
        <Input
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          value={form.phone}
          onChange={(event) => setForm({ ...form, phone: event.target.value })}
        />
      </FormField>
      <div className="grid gap-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="accept-terms"
            checked={form.acceptTerms}
            aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
            onCheckedChange={(checked) => setForm({ ...form, acceptTerms: checked === true })}
          />
          <Label htmlFor="accept-terms" className="font-normal">
            {p.acceptTerms}
          </Label>
        </div>
        {errors.acceptTerms && (
          <p id="terms-error" role="alert" className="text-sm text-danger">
            {errors.acceptTerms}
          </p>
        )}
      </div>
      <Button type="submit" size="lg" loading={signup.isPending}>
        {p.submit}
      </Button>
    </form>
  );
}
